import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { ogImage as toOgImage } from '../lib/cdn';

// GIWAHS Universal SEO Component
// Drop-in for every page. Sets title, meta description, canonical, OG, Twitter Card,
// and an optional JSON-LD structured-data block.
//
// Props:
//   title         — page title (will be suffixed " — GIWAHS")
//   description   — meta description (~150–160 chars ideal)
//   image         — absolute OG image URL
//   type          — og:type (default 'website'; 'article' for guides; 'product' for products)
//   canonical     — full canonical URL (auto-derived from window if not provided)
//   noindex       — true to add <meta name="robots" content="noindex">
//   keywords      — comma-separated string (optional, low-weight but harmless)
//   jsonLd        — single object OR array of structured-data objects (rendered as JSON-LD)
//   breadcrumbs   — [{name, url}] — will be appended as BreadcrumbList JSON-LD
//
// Site-wide defaults are configurable via REACT_APP_SITE_URL + REACT_APP_DEFAULT_OG_IMAGE.

const SITE_NAME = 'GIWAHS';
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://giwahs.com').replace(/\/$/, '');
const DEFAULT_OG_IMAGE = process.env.REACT_APP_DEFAULT_OG_IMAGE
  || 'https://static.prod-images.emergentagent.com/jobs/78d6f77c-7a7f-48ae-b71b-ec8e8809cfb7/images/4ad6059937baee7d67f0535497c968eccfe7fc143d9d31462a48e40d95fb3cda.png';
const DEFAULT_DESCRIPTION = 'GIWAHS — global sourcing intelligence platform. Discover verified factories, compare suppliers, and source fashion, streetwear, workwear, activewear, and accessories worldwide.';
const TWITTER_HANDLE = '@giwahs';

const buildAbsolute = (path) => {
  if (!path) return SITE_URL;
  if (/^https?:\/\//.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image,
  type = 'website',
  canonical,
  noindex = false,
  keywords,
  jsonLd,
  breadcrumbs,
}) => {
  const location = useLocation();
  const pageTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Global Sourcing Intelligence Platform`;
  const url = canonical || `${SITE_URL}${location.pathname}`;
  // Always serve OG/Twitter image at 1200×630 via the CDN/resizer layer.
  const ogImage = toOgImage(buildAbsolute(image || DEFAULT_OG_IMAGE));

  // Build JSON-LD array (incl. breadcrumbs if provided)
  const ldBlocks = [];
  if (jsonLd) {
    if (Array.isArray(jsonLd)) ldBlocks.push(...jsonLd);
    else ldBlocks.push(jsonLd);
  }
  if (breadcrumbs && breadcrumbs.length) {
    ldBlocks.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.name,
        item: buildAbsolute(b.url),
      })),
    });
  }

  return (
    <Helmet prioritizeSeoTags>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {ldBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

// Convenience exports for callers
export const siteUrl = SITE_URL;
export const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description: DEFAULT_DESCRIPTION,
  sameAs: [],
};
export const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/products?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export default SEO;
